import React from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";
import { Player, ControlBar, BigPlayButton, LoadingSpinner, PlayToggle, VolumeMenuButton, FullscreenToggle, CurrentTimeDisplay, DurationDisplay, ProgressControl } from "video-react";

import "../../../node_modules/video-react/dist/video-react.css";
import WideContainer from "../../components/WideContainer";
import { buildModel, castTimes, arcDetail, sceneAt, memoize } from "./scenes/sceneModel";
import { fetchScenes, sceneLang } from "./scenes/scenesApi";
import { dragColumns } from "./scenes/columns";
import { summaryOf, tagCounts, importantCast, keyMoments, momentAt, castReportCsv } from "./overview/overviewModel";
import { CastCard, SummaryCard, KeyMoments, ArcList } from "./overview/OverviewCards";
import "./Queries.css";
import "./Scenes.css";
import "./Overview.css";

// The Overview tab: what the video is about (the video summary), its key moments and story arcs, and its important
// characters, beside a player that stays in view. Same data (GET /scenes) and column layout as the Scenes tab.
class Index extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      data: null,
      playing: -1, // key moment at the player's time
      sceneNow: -1, // scene at the player's time, -1 before the video starts
      openArc: null, // story arc open in place
      leftW: null, // dragged left column width, null = automatic
      resizing: false,
    };

    this.byLang = {}; // lang -> normalized /scenes response
    this.getModel = memoize((data, sentences, dic) => (data ? buildModel(data, sentences, dic) : null));
    this.getSummary = memoize(summaryOf);
    this.getTimes = memoize(castTimes);
    this.getCast = memoize(importantCast);
    this.getMoments = memoize(keyMoments);
    this.getTags = memoize(tagCounts);
    this.getArc = memoize((model, k) => (model && k !== null ? arcDetail(model, k) : null));
    this.getFolder = memoize((projectFolder) => JSON.parse(localStorage.getItem("user")).user_folder + "/" + projectFolder);
  }

  componentDidMount() {
    this.unmounted = false;
    this.player.subscribeToStateChange(this.handleStateChange.bind(this));
    this.getItem();
  }

  componentDidUpdate(prevProps) {
    if (this.props.videosrc !== prevProps.videosrc) {
      this.player.load();
    }
    if (sceneLang(this.props.i18n) !== this.dataLang) this.getItem();
    const model = this.model();
    if (model && model !== this.reportedModel) {
      this.reportedModel = model;
      this.props.onStats &&
        this.props.onStats({ scenes: model.scenes.length, arcs: model.arcs.length, characters: model.characterNames.length, duration: model.duration });
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.stopDrag && this.stopDrag();
  }

  // scenes and summary in the UI language; the current ones stay on screen while another language loads
  getItem() {
    const lang = sceneLang(this.props.i18n);
    this.dataLang = lang;
    if (this.byLang[lang]) {
      this.setState({ data: this.byLang[lang] });
      return;
    }
    if (!this.state.data) this.setState({ loading: true });
    fetchScenes(this.props.project.project_id, lang)
      .then((data) => {
        this.byLang[lang] = data;
        if (lang !== this.dataLang || this.unmounted) return;
        this.setState({ loading: false, data: data });
      })
      .catch((error) => {
        if (!this.unmounted) this.setState({ loading: false });
        console.log(error);
      });
  }

  model() {
    return this.getModel(this.state.data, this.props.sentences.items, this.props.characters.dic);
  }

  summary() {
    return this.getSummary(this.state.data);
  }

  moments() {
    return this.getMoments(this.summary(), this.model(), this.props.characters.items);
  }

  handleStateChange(state) {
    // share the time with the other tabs (once the video has a time of its own)
    if (this.props.onTime && (state.hasStarted || state.currentTime > 0)) this.props.onTime(state.currentTime);
    const model = this.model();
    const k = momentAt(this.moments(), state.currentTime);
    const x = model && state.hasStarted ? sceneAt(model, state.currentTime) : -1;
    if (k !== this.state.playing || x !== this.state.sceneNow) this.setState({ playing: k, sceneNow: x });
  }

  playAt = (t) => {
    this.player.seek(t);
    this.player.play();
  };

  playMoment = (k) => {
    const m = this.moments()[k];
    if (!m) return;
    this.setState({ playing: k });
    this.playAt(m.s);
  };

  toggleArc = (k) => this.setState((st) => ({ openArc: st.openArc === k ? null : k }));

  startResize = (e) => {
    this.setState({ resizing: true });
    this.stopDrag = dragColumns(
      e,
      (w) => this.setState({ leftW: w }),
      () => this.setState({ resizing: false })
    );
  };

  resetResize = () => this.setState({ leftW: null });

  downloadCastReport = () => {
    const { project, characters } = this.props;
    const model = this.model();
    const duration = project.duration > 0 ? project.duration : model ? model.duration : 0;
    const csv = castReportCsv(characters.items, this.getTimes(characters.items, this.props.sentences.items), characters.actor, duration, this.props.t);
    const a = document.createElement("a");
    //%EF%BB%BF is the UTF-8 BOM, so Excel opens accented names correctly
    a.href = "data:text/csv;charset=utf-8,%EF%BB%BF" + encodeURIComponent(csv);
    a.download = project.project_name + " - cast report.csv";
    a.click();
  };

  // the project's folder on the CDN
  folder() {
    return this.getFolder(this.props.project.project_folder);
  }

  render() {
    const { t, project, characters } = this.props;
    const { loading, leftW, playing, sceneNow, openArc } = this.state;
    const model = this.model();
    const summary = this.summary();
    const moments = this.moments();
    const tags = this.getTags(model);
    const cast = this.getCast(summary, characters.items, this.getTimes(characters.items, this.props.sentences.items));
    const hasScenes = !!model && model.scenes.length > 0;
    const arcs = model ? model.arcs : [];
    const folder = this.folder();
    return (
      <div className="overviewTab">
        <WideContainer>
          <div className="sc-page ov-page" style={leftW === null ? undefined : { "--sc-drag": leftW + "px" }}>
            <div className="sc-body">
              <div
                className="sc-grip"
                data-on={this.state.resizing}
                title={t("Drag to resize columns · double-click to reset")}
                onMouseDown={this.startResize}
                onDoubleClick={this.resetResize}
              />
              <div className="sc-col">
                <div className="sc-player">
                  <Player
                    ref={(player) => {
                      this.player = player;
                    }}
                    autoPlay={false}
                    startTime={this.props.startTime}
                    poster={this.props.startTime > 0 ? undefined : folder + "/video_lowres_thumbnail.jpg"}
                    src={this.props.videosrc}
                  >
                    <BigPlayButton position="center" />
                    <LoadingSpinner />
                    <ControlBar autoHide={false} disableDefaultControls>
                      <PlayToggle key="play-toggle" order={1} />
                      <VolumeMenuButton key="volume-menu-button" order={4} vertical />
                      <CurrentTimeDisplay key="current-time-display" order={5.1} />
                      <DurationDisplay key="duration-display" order={7.3} />
                      <ProgressControl key="progress-control" order={6} />
                      <FullscreenToggle key="fullscreen-toggle" order={8} />
                    </ControlBar>
                  </Player>
                </div>
                <CastCard t={t} cast={cast} folder={folder} onReport={this.downloadCastReport} />
              </div>
              <div className="sc-col">
                {loading && <CircularProgress style={{ margin: "40px auto", display: "block" }} />}
                {!loading && (summary || hasScenes) && <SummaryCard t={t} summary={summary} tags={tags} hasScenes={hasScenes} />}
                {!loading && moments.length > 0 && (
                  <KeyMoments t={t} moments={moments} playing={playing} projectId={project.project_id} folder={folder} onPlay={this.playMoment} />
                )}
                {!loading && arcs.length > 0 && (
                  <ArcList
                    t={t}
                    arcs={arcs}
                    open={openArc}
                    detail={this.getArc(model, openArc)}
                    sceneNow={sceneNow}
                    onToggle={this.toggleArc}
                    onStep={this.playAt}
                  />
                )}
                {!loading && !summary && !hasScenes && <div className="sc-empty">{t("No overview for this video yet")}</div>}
              </div>
            </div>
          </div>
        </WideContainer>
      </div>
    );
  }
}

const mapState = (state) => {
  return {
    sentences: state.sentences,
    characters: state.characters,
  };
};

export default withTranslation()(connect(mapState)(Index));
